/**
 * @fileoverview MarketingCrossDomainLogin — the canonical cross-domain login
 * surface for every Colony marketing site (MKT-LOGIN-XDOMAIN-001, 2026-06-29).
 *
 * Rendered at the marketing site's `/login` route. The visitor enters their
 * company subdomain + credentials; on success they're redirected to their own
 * tenant ({sub}.<app>.com). Promoted from pos-web's ~355-line app-local
 * `MarketingLoginPage` so all 8 marketing apps converge on ONE implementation
 * (Gen-0); each app ships only a thin wrapper that injects brand + auth + SEO.
 *
 * Decoupling: login-shell stays free of react-i18next / react-router /
 * marketing-shell. Strings come via the injected `t`; query params are read
 * from `window.location` (no router); SEO + analytics are injected slots.
 *
 * @module @puffin-core/login-shell/components/MarketingCrossDomainLogin
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LoginShell } from './LoginShell.jsx';
import { SubdomainPrompt } from './SubdomainPrompt.jsx';
import { SubdomainLookupModal } from './SubdomainLookupModal.jsx';
import { useSubdomainDetection } from '../hooks/useSubdomainDetection.js';
import {
  useSubdomainValidation,
  defaultSubdomainFormatValidator,
} from '../hooks/useSubdomainValidation.js';
import { usePasswordResetClient } from '../hooks/usePasswordResetClient.js';
import { usePageLanguage } from '../hooks/usePageLanguage.js';
import { createPublicJsonPoster } from '../utils/publicJsonPoster.js';
import { DEFAULT_ACCESS_REQUEST_ENDPOINT } from '../hooks/useAccessRequestSubmit.js';

const DEFAULT_SUBDOMAIN_LOOKUP_ENDPOINT = '/api/v1/auth/subdomain-lookup';
const DEFAULT_PASSWORD_RESET_BASE = '/api/v1/auth/tenant/password-reset';
// MKT-LOGIN-XDOMAIN-001/HANDOFF: after auth, the marketing login calls this to
// mint a one-time tenant-session token + get the tenant `…/?cat=` URL to redirect
// to (the tenant origin exchanges it). Replaces the broken apex-localStorage hop.
const DEFAULT_HANDOFF_ENDPOINT = '/api/v1/auth/cross-app/handoff';

/**
 * Build the tenant origin `{scheme}://{sub}.{apex}` for a subdomain. On
 * localhost dev resolves to `{sub}.localhost:{port}`. Exported for direct unit
 * coverage (it's only otherwise reached via LoginShell's post-login redirect).
 */
export function buildSubdomainUrl(subdomain) {
  const { protocol, hostname, port } = window.location;
  if (hostname.includes('localhost')) {
    return `${protocol}//${subdomain}.localhost${port ? `:${port}` : ''}`;
  }
  const apex = hostname.replace(/^www\./, '');
  return `${protocol}//${subdomain}.${apex}`;
}

/**
 * Build the post-login redirect target. Appends `returnUrl` only when it is a
 * safe relative path (open-redirect protection — rejects `//evil.com` and
 * absolute URLs). Exported for direct unit coverage.
 *
 * @param {string} subdomain
 * @param {string} returnUrl
 * @returns {string}
 */
export function buildCrossDomainRedirectUrl(subdomain, returnUrl) {
  const safeReturn = returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '';
  if (subdomain) return `${buildSubdomainUrl(subdomain)}${safeReturn || ''}`;
  return safeReturn || '/';
}

/**
 * The pre-auth JSON POST — `utils/publicJsonPoster`, the ONE copy login-shell carries (promoted from
 * this file by UNREACHABLE-MODULE-BURNDOWN-001 L16 when the tenant login's "Request access" modal
 * needed the same call). Its docblock records why it reads the RAW response through an injected
 * fetch seam rather than a facade. The label keeps an absent-`fetch` rejection naming THIS surface.
 */
const postJson = createPublicJsonPoster('MarketingCrossDomainLogin');

// A browser-only component: `window` always exists where it renders, so a server-render guard here was a
// branch no run could reach (removed 2026-10-11, LOGIN-CONVERGENCE, when this file was touched for W12).
function readQueryParam(name) {
  return new URLSearchParams(window.location.search).get(name) || '';
}

/**
 * The `?email=` / `?subdomain=` / `?returnUrl=` entry params. Read once from
 * `window.location` at mount (no router dependency — see the module docblock).
 * Module-private.
 */
function useMarketingLoginQueryParams() {
  const urlEmail = useMemo(() => readQueryParam('email'), []);
  const urlSubdomain = useMemo(() => readQueryParam('subdomain'), []);
  const returnUrl = useMemo(() => readQueryParam('returnUrl'), []);

  return { urlEmail, urlSubdomain, returnUrl };
}

/**
 * Which tenant the visitor is signing in to: the subdomain (from the URL, the
 * remembered one, or typed), whether they're actively editing it, and its live
 * resolution against the tenant registry. Module-private.
 */
function useMarketingSubdomainSelection({ subdomainPersistence, urlSubdomain }) {
  const { storedSubdomain, hasStoredSubdomain, clearSubdomain } =
    useSubdomainDetection(subdomainPersistence);
  const subdomainValidation = useSubdomainValidation({
    validateFormat: defaultSubdomainFormatValidator,
  });

  const initialSubdomain = urlSubdomain || (hasStoredSubdomain ? storedSubdomain : '');
  const [subdomain, setSubdomain] = useState(initialSubdomain || '');
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(!initialSubdomain);

  useEffect(() => {
    if (!subdomain && hasStoredSubdomain && storedSubdomain && !urlSubdomain) {
      setSubdomain(storedSubdomain);
      setIsEditingSubdomain(false);
    }
  }, [hasStoredSubdomain, storedSubdomain, urlSubdomain, subdomain]);

  // AUTH-LOGIN-FIDELITY-001: depend on the STABLE validateSubdomain/clearValidation
  // callbacks, NOT the whole validation object (whose identity changes on every
  // state transition, which re-issued redundant resolve fetches).
  const { validateSubdomain, clearValidation } = subdomainValidation;
  useEffect(() => {
    if (subdomain && subdomain.trim()) {
      validateSubdomain(subdomain.trim());
    } else {
      clearValidation();
    }
  }, [subdomain, validateSubdomain, clearValidation]);

  const handleSubdomainChange = useCallback((value) => {
    setSubdomain(value);
    setIsEditingSubdomain(true);
  }, []);

  const handleUseDifferentCompany = useCallback(() => {
    clearSubdomain();
    setSubdomain('');
    setIsEditingSubdomain(true);
  }, [clearSubdomain]);

  return {
    subdomain,
    isEditingSubdomain,
    storedSubdomain,
    hasStoredSubdomain,
    subdomainValidation,
    handleSubdomainChange,
    handleUseDifferentCompany,
  };
}

/**
 * Page-view + auth-event tracking for the marketing login. `analytics` is an
 * injected slot, so every arm is optional. Module-private.
 */
function useMarketingLoginAnalytics({ analytics, t }) {
  const trackPageView = analytics?.trackPageView;
  const track = analytics?.track;

  useEffect(() => {
    if (trackPageView) trackPageView('/login', t('login.pageTitle'));
  }, [trackPageView, t]);

  const handleAuthSuccess = useCallback(
    (result) => {
      if (track) track('auth_success', { userId: result?.user?.userId });
    },
    [track],
  );

  const handleAuthEvent = useCallback(
    (type, payload) => {
      if (track) track(`auth.${type}`, payload);
    },
    [track],
  );

  return { handleAuthSuccess, handleAuthEvent };
}

/**
 * Assembles the config objects `<LoginShell>` consumes on the marketing surface:
 * the password-reset callbacks, the subdomain-lookup network callback +
 * lookup-modal state, and the redirect / credential-persistence / subdomain
 * configs. The access request is not assembled here: `<LoginShell>` submits it
 * itself, to the `accessRequestEndpoint` this component forwards. Module-private.
 */
function useMarketingLoginShellConfig({
  urlEmail,
  returnUrl,
  subdomain,
  handleSubdomainChange,
  subdomainLookupEndpoint,
  handoffEndpoint,
  passwordResetBasePath,
}) {
  const passwordResetCallbacks = usePasswordResetClient({ basePath: passwordResetBasePath });

  const [isLookupModalOpen, setIsLookupModalOpen] = useState(false);
  // EMAIL-LOCALE-ZONE-001 owner ruling #9: the reminder is written in the language this page is showing.
  const language = usePageLanguage();

  const callbacks = useMemo(
    () => ({
      onSubdomainLookupSubmit: async (email) => {
        await postJson(subdomainLookupEndpoint, { email, language });
      },
      openLookupModal: () => setIsLookupModalOpen(true),
      closeLookupModal: () => setIsLookupModalOpen(false),
    }),
    [subdomainLookupEndpoint, language],
  );

  // MKT-LOGIN-XDOMAIN-001/HANDOFF: on success, establish the session on the
  // TENANT origin via a one-time server-minted handoff token (the tenant
  // exchanges `?cat=…`), instead of writing tokens to THIS (marketing) origin's
  // localStorage — which can't cross origins and was the original "logged-out on
  // the tenant" bug. `returnUrl` is honored only when relative (open-redirect
  // safety). A handoff failure THROWS → surfaced as an auth error (never a silent
  // redirect to a session-less tenant).
  const redirectConfig = useMemo(
    () => ({
      mode: 'cross-domain',
      buildUrl: async (result, { subdomain: sub }) => {
        const accessToken = result?.accessToken || result?.token || null;
        const safeReturn =
          returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : undefined;
        const data = await postJson(
          handoffEndpoint,
          { subdomain: sub, ...(safeReturn ? { returnPath: safeReturn } : {}) },
          accessToken,
        );
        if (!data || !data.targetUrl) {
          throw new Error('Sign-in could not complete. Please try again.');
        }
        return data.targetUrl;
      },
      // N152: a held sign-in (403 MFA_SETUP_REQUIRED) enrols at the company's own sign-in page — the target shape the
      // shared-terminal handoff uses. Its enrolment screen is the shared one; the apex can hold no session to do it.
      buildTenantLoginUrl: (sub) => `${buildSubdomainUrl(sub)}/login`,
    }),
    [returnUrl, handoffEndpoint],
  );

  const credentialPersistenceConfig = useMemo(
    () => (urlEmail ? { load: () => ({ email: urlEmail }) } : undefined),
    [urlEmail],
  );

  // AUTH-LOGIN-FIDELITY-001: CONTROLLED subdomain — this component owns the value
  // and useLoginForm derives the submitted subdomain from it on every render.
  const subdomainConfig = useMemo(
    () => ({ source: 'input', value: subdomain, onChange: handleSubdomainChange }),
    [subdomain, handleSubdomainChange],
  );

  return {
    passwordResetCallbacks,
    isLookupModalOpen,
    callbacks,
    redirectConfig,
    credentialPersistenceConfig,
    subdomainConfig,
  };
}

/**
 * @param {object} props
 * @param {(params?: object) => {auth: object}} props.useAuth
 *   The hook produced by `createMarketingLoginAuth` (stable across renders). It
 *   carries no passkey leg: a passkey is bound to the tenant origin's rpId, so the
 *   marketing surface offers password + MFA only (USER-FACING-GEMS L1b).
 * @param {(key: string, opts?: object) => string} props.t - Translation function.
 * @param {string} props.appName - Brand name (e.g. 'PalmPuffin').
 * @param {string} props.appKey - The app's `APP_REGISTRY` key (e.g. 'palm'). REQUIRED (W17): once a
 *   company resolves, the sign-in offers "Not your company? Sign in elsewhere", which returns to this
 *   app's tenant-neutral apex sign-in — the shell refuses to render it for an app it cannot name.
 * @param {string} props.baseDomain - Subdomain suffix label (e.g. '.palmpuffin.com').
 * @param {string} [props.appSubtitle] - Overrides `t('login.subtitle')`.
 * @param {{getStoredSubdomain: Function, clearStoredSubdomain: Function}} [props.subdomainPersistence]
 * @param {{trackPageView?: Function, track?: Function}} [props.analytics]
 * @param {React.ReactNode} [props.seoSlot] - App-built SEO head (MarketingSeo).
 * @param {string} [props.subdomainLookupEndpoint]
 * @param {string} [props.passwordResetBasePath]
 * @param {string} [props.accessRequestEndpoint]
 * @param {string} [props.startTrialHref] - "Start a free trial" target (default '/contact').
 * @param {string} [props.homeHref] - "Back to home" target (default '/').
 */
export function MarketingCrossDomainLogin({
  useAuth,
  t,
  appName,
  appKey,
  baseDomain,
  appSubtitle,
  subdomainPersistence,
  analytics,
  seoSlot = null,
  subdomainLookupEndpoint = DEFAULT_SUBDOMAIN_LOOKUP_ENDPOINT,
  passwordResetBasePath = DEFAULT_PASSWORD_RESET_BASE,
  accessRequestEndpoint = DEFAULT_ACCESS_REQUEST_ENDPOINT,
  handoffEndpoint = DEFAULT_HANDOFF_ENDPOINT,
  startTrialHref = '/contact',
  homeHref = '/',
}) {
  const { urlEmail, urlSubdomain, returnUrl } = useMarketingLoginQueryParams();

  const {
    subdomain,
    isEditingSubdomain,
    storedSubdomain,
    hasStoredSubdomain,
    subdomainValidation,
    handleSubdomainChange,
    handleUseDifferentCompany,
  } = useMarketingSubdomainSelection({ subdomainPersistence, urlSubdomain });

  const { handleAuthSuccess, handleAuthEvent } = useMarketingLoginAnalytics({ analytics, t });

  const { auth } = useAuth({
    onSuccess: handleAuthSuccess,
    onAuthEvent: handleAuthEvent,
  });

  const {
    passwordResetCallbacks,
    isLookupModalOpen,
    callbacks,
    redirectConfig,
    credentialPersistenceConfig,
    subdomainConfig,
  } = useMarketingLoginShellConfig({
    urlEmail,
    returnUrl,
    subdomain,
    handleSubdomainChange,
    subdomainLookupEndpoint,
    handoffEndpoint,
    passwordResetBasePath,
  });

  const subdomainPromptSlot = (
    <SubdomainPrompt
      subdomain={subdomain}
      onSubdomainChange={handleSubdomainChange}
      baseDomain={baseDomain}
      onLookupOpen={callbacks.openLookupModal}
      validation={{
        isValid: subdomainValidation.isValid,
        isValidating: subdomainValidation.isValidating,
        companyName: subdomainValidation.companyName,
        error: subdomainValidation.error,
      }}
      welcomeBack={
        !isEditingSubdomain && hasStoredSubdomain
          ? { subdomain: storedSubdomain, onClear: handleUseDifferentCompany }
          : undefined
      }
    />
  );

  // When the entered subdomain resolves to a real tenant, surface BOTH its name
  // (for the company banner) and its companyId. LoginShell/LoginForm gate the
  // "request access" + "not your company" affordances on a truthy companyId —
  // without it the already-wired access-request submission was unreachable
  // in the marketing surface (orphaned-but-built).
  const resolvedCompanyInfo = subdomainValidation.isValid ? subdomainValidation.companyInfo : null;
  const companyName = resolvedCompanyInfo?.name || null;
  const companyId = resolvedCompanyInfo?.companyId || null;
  const hasValidContext = Boolean(urlSubdomain) || subdomainValidation.isValid;

  const footerSlot = (
    <div className="auth-card-links">
      {!hasValidContext && (
        <>
          <p className="text text-sm text-center text-muted">
            {t('login.links.newToApp', { app: appName, defaultValue: `New to ${appName}?` })}{' '}
            <a href={startTrialHref} className="nav-link text-primary">
              {t('login.links.startTrial', { defaultValue: 'Start a free trial' })}
            </a>
          </p>
          <p className="text text-sm text-center text-muted">
            <button
              type="button"
              className="btn btn-link text-sm"
              onClick={callbacks.openLookupModal}
            >
              {t('login.links.forgotSubdomain', { defaultValue: 'Forgot your company subdomain?' })}
            </button>
          </p>
        </>
      )}
      <a href={homeHref} className="btn btn-link text-sm auth-back-link">
        {t('login.links.backToHome', { defaultValue: 'Back to Home' })}
      </a>
    </div>
  );

  return (
    <>
      {seoSlot}
      <LoginShell
        // W12: every marketing site renders its pages inside marketing-shell's own `<main>`.
        embedded
        appName={appName}
        appKey={appKey}
        appSubtitle={appSubtitle || t('login.subtitle', { defaultValue: '' })}
        mode="cross-domain"
        subdomain={subdomain}
        companyId={companyId}
        companyName={companyName}
        auth={auth}
        // No passkey method on the marketing apex: a passkey is bound to the tenant origin's
        // rpId, so none can be asserted here (USER-FACING-GEMS L1b). `cross-domain` mode
        // defaults `supportsPasskey` to true, which rendered a Passkey tab that opened an empty
        // panel — it must be said, not defaulted.
        supportsPasskey={false}
        subdomainPromptSlot={subdomainPromptSlot}
        subdomainConfig={subdomainConfig}
        credentialPersistenceConfig={credentialPersistenceConfig}
        redirectConfig={redirectConfig}
        onAuthEvent={handleAuthEvent}
        {...passwordResetCallbacks}
        accessRequestEndpoint={accessRequestEndpoint}
        footerSlot={footerSlot}
      />
      <SubdomainLookupModal
        isOpen={isLookupModalOpen}
        onClose={callbacks.closeLookupModal}
        onLookupSubmit={callbacks.onSubdomainLookupSubmit}
      />
    </>
  );
}

MarketingCrossDomainLogin.propTypes = {
  useAuth: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  appName: PropTypes.string.isRequired,
  appKey: PropTypes.string.isRequired,
  baseDomain: PropTypes.string.isRequired,
  appSubtitle: PropTypes.string,
  subdomainPersistence: PropTypes.shape({
    getStoredSubdomain: PropTypes.func,
    clearStoredSubdomain: PropTypes.func,
  }),
  analytics: PropTypes.shape({
    trackPageView: PropTypes.func,
    track: PropTypes.func,
  }),
  seoSlot: PropTypes.node,
  subdomainLookupEndpoint: PropTypes.string,
  passwordResetBasePath: PropTypes.string,
  accessRequestEndpoint: PropTypes.string,
  handoffEndpoint: PropTypes.string,
  startTrialHref: PropTypes.string,
  homeHref: PropTypes.string,
};

export default MarketingCrossDomainLogin;
